/**
 * Filter Exclusion Utility for Context-Aware Asset Filter Management
 * This utility provides a unified approach to filter management across different
 * contexts while maintaining a single source of truth from useAssetFilterOptions.
 * Usage:
 * - Asset Table: Show all filters (no exclusions)
 * - Query Builder: Exclude complex filters not suitable for query building
 * - Alerts: Exclude filters not relevant for alerting
 */

import type { FilterOption, GroupOption } from '@/hooks/api/useGetAssetFilters';

// Define available filter contexts with strict typing
export type FilterContext = 'asset-table' | 'query-builder';

// Configuration for filter exclusions by context
type FilterExclusionConfig = {
  readonly [K in FilterContext]: readonly string[];
};

/**
 * Context-specific filter exclusion configuration
 * Each context can exclude specific filter types that don't make sense
 * for that particular use case, while maintaining the dynamic nature
 * of the filter system.
 */
const FILTER_EXCLUSION_CONFIG: FilterExclusionConfig = {
  // Asset table shows all available filters
  'asset-table': ['source'],
  // Query builder excludes complex multi-select filters that don't translate well to queries
  'query-builder': ['source'] as const,
} as const;

/**
 * Type for the asset filter options structure
 */
type AssetFilterOptions = Record<string, (FilterOption | GroupOption)[]>;

/**
 * Filters out excluded filter options based on the specified context
 * @param filterOptions - Raw filter options from useAssetFilterOptions
 * @param context - The context in which filters will be used
 * @returns Filtered options with excluded filters removed
 */
export function excludeFilters(
  filterOptions: AssetFilterOptions | undefined,
  context: FilterContext
): AssetFilterOptions {
  // Return empty object if no filter options provided
  if (!filterOptions) {
    return {};
  }
  // Get exclusion list for the specified context
  const excludedKeys =
    FILTER_EXCLUSION_CONFIG[context as keyof FilterExclusionConfig] || [];
  // Filter out excluded keys and return the result
  return Object.fromEntries(
    Object.entries(filterOptions).filter(([key]) => !excludedKeys.includes(key))
  );
}

/**
 * Get human-readable labels for filter contexts
 * Useful for debugging and documentation
 */
export function getContextLabel(context: FilterContext): string {
  const labels: Record<FilterContext, string> = {
    'asset-table': 'Asset Table',
    'query-builder': 'Query Builder',
  };

  return labels[context] || context;
}

/**
 * Get the list of excluded filter keys for a given context
 * Useful for debugging and understanding what filters are available
 */
export function getExcludedFilters(context: FilterContext): readonly string[] {
  return FILTER_EXCLUSION_CONFIG[context as keyof FilterExclusionConfig] || [];
}

/**
 * Check if a specific filter is excluded in a given context
 */
export function isFilterExcluded(
  filterKey: string,
  context: FilterContext
): boolean {
  const excludedKeys =
    FILTER_EXCLUSION_CONFIG[context as keyof FilterExclusionConfig] || [];
  return excludedKeys.includes(filterKey);
}
